import React, { useEffect, useState, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Box, Button, Flex, HStack, Heading, Image, InputLeftAddon, Link, Text } from '@chakra-ui/react'
import { FiLock, FiUser } from 'react-icons/fi'

import { Input } from '@/components/Form/'
import { authSelector, loginWithEmail, loginWithSiemensID } from '@/stores/auth/authSlice'

//Hooks
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

//Constant
import config, { APP_MODE } from '@/config'
import useAuth from '@/features/auth/useAuth'
import storage from '@/utils/storage'
import { camelToWords } from '@/utils/stringHelper'
import { useTranslation } from 'react-i18next'

const LoginForm = () => {
  const { t, i18n } = useTranslation()

  const isProduction = ![APP_MODE.DEVELOPMENT, APP_MODE.STAGING].includes(config.mode)
  const dispatch = useDispatch()
  const methods = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  })
  const location = useLocation()
  const params = new URLSearchParams(location.search)

  const { isLoading, siemensLoginStarted } = useSelector(authSelector)
  const [isLoadingSiemensIdLogin, setIsLoadingSiemensIdLogin] = useState(false)
  const hasLoggedInRef = useRef(false)

  const onSubmit = (data) => {
    if (![APP_MODE.DEVELOPMENT, APP_MODE.STAGING].includes(config.mode) && data.email.includes('siemens')) {
      toast.error('Email login is disabled in production for Siemens user, please use Siemens ID Login instead ! ')
      return
    }

    //Set Redirect
    if (location?.state?.from) {
      storage.setRedirect(location.state.from)
    }

    if (data.email === '' || data.password === '') {
      toast.error('Missing Email / Password ! ')
      return
    }

    dispatch(loginWithEmail(data))
  }

  useEffect(() => {
    const loginCode = params.get('code')
    if (isLoading || hasLoggedInRef.current) return

    hasLoggedInRef.current = true

    if (loginCode) {
      try {
        dispatch(showLoadingOverlay())
        dispatch(loginWithSiemensID({ code: loginCode }))
      } catch (err) {
        dispatch(hideLoadingOverlay())
      }
    }
  }, [dispatch, params, isLoading])

  const handleLoginWithSiemensID = async () => {
    try {
      setIsLoadingSiemensIdLogin(true)
      dispatch(showLoadingOverlay())

      const accessUrl = config.siemensIdLoginUrl

      window.location.replace(accessUrl)
    } catch (err) {
      console.log(err)
      toast.error('Login with Siemens ID failed.')
    } finally {
      setIsLoadingSiemensIdLogin(false)
      dispatch(hideLoadingOverlay())
    }
  }

  const showReadMeFile = () => {
    window.open(`https://sihkeapproval.siemens.com.hk/READMEEX`, '_blank')
  }

  return (
    <Flex direction="column" bg="brand.800" p={[6, 12]} minW={[0, 700]} color={'white'}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Flex direction="column" gap="2">
            <Box mb="20px">
              <Image
                w="150px"
                objectFit="cover"
                src={
                  // eslint-disable-next-line max-len
                  'https://www.siemens-home.bsh-group.com.hk/store/medias/sys_master/root/h63/hcd/10563534323742/GL-SIE-Logo-Siemens-Petrol-RGB-BSH-Reshape-2021.png'
                }
              />
            </Box>
            {config.mode !== APP_MODE.PRODUCTION && (
              <Heading size="sm">Environment: {camelToWords(config.mode)}</Heading>
            )}

            <Text>{t('SIGN_IN_NOTES')}[Vendor Only]</Text>
            {!isProduction && (
              <Input
                name="email"
                type="email"
                placeholder={t('SIGN_IN_EMAIL')}
                canHighLight={false}
                autoComplete={'email'}
                variant="brand"
                leadingIcon={
                  <InputLeftAddon>
                    <FiUser />
                  </InputLeftAddon>
                }
              />
            )}
            {!isProduction && (
              <Input
                name="password"
                type="password"
                placeholder={t('SIGN_IN_PASSWORD')}
                canHighLight={false}
                variant="brand"
                leadingIcon={
                  <InputLeftAddon>
                    <FiLock />
                  </InputLeftAddon>
                }
                autoComplete={'password'}
              />
            )}

            {!isProduction && (
              <Link color="teal" href="/auth/requestPasswordReset">
                {t('SIGN_IN_FORGOT_PASSWORD')}
              </Link>
            )}
            <Flex gap="2">
              {!isProduction && (
                <Button isLoading={isLoading} type="submit" variant={'brand'}>
                  {t('SIGN_IN_LOGIN')}
                </Button>
              )}

              <Button
                isLoading={isLoadingSiemensIdLogin}
                loadingText="Loading"
                onClick={handleLoginWithSiemensID}
                variant={'brand'}
              >
                {t('SIGN_IN_LOGIN_WITH_SIEMENS_ID')}
              </Button>
            </Flex>
            <HStack>
              <Button loadingText="Loading" size="sm" onClick={showReadMeFile}>
                {t('SIGN_IN_LOGIN_GUIDE')}
              </Button>
            </HStack>
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  )
}

export default LoginForm
